import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Warning, CalendarBlank, Clock, ShieldWarning } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "subscription-warning-dismissed";

const SubscriptionWarningPopup = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const checkWarning = useCallback(async () => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    try {
      const res = await axios.get(`${API_URL}/api/customer/subscription-warning`);
      if (res.data?.show_warning) {
        setData(res.data);
        setOpen(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    checkWarning();
  }, [checkWarning]);

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  if (!data) return null;

  const isCritical = data.severity === "critical";
  const sym = data.currency === "USD" ? "$" : "\u20ba";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="subscription-warning-popup"
      >
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isCritical
                ? "bg-red-100"
                : "bg-amber-100"
            }`}>
              {isCritical ? (
                <ShieldWarning size={36} weight="duotone" className="text-red-600" />
              ) : (
                <Warning size={36} weight="duotone" className="text-amber-600" />
              )}
            </div>
          </div>
          <DialogTitle className={`text-center text-lg ${
            isCritical ? "text-red-700" : "text-amber-700"
          }`}>
            {isCritical
              ? "Aboneliginiz Sona Ermek Uzere!"
              : "Abonelik Yenileme Hatirlatmasi"}
          </DialogTitle>
          <DialogDescription className="text-center text-slate-500">
            {data.plan_name} planinizin bitis tarihine kalan sure
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Days Counter */}
          <div className={`text-center py-4 rounded-xl ${
            isCritical
              ? "bg-red-50 border-2 border-red-200"
              : "bg-amber-50 border-2 border-amber-200"
          }`}>
            <div className={`text-5xl font-bold ${
              isCritical ? "text-red-600" : "text-amber-600"
            }`}>
              {data.days_left}
            </div>
            <div className={`text-sm font-medium mt-1 ${
              isCritical ? "text-red-500" : "text-amber-500"
            }`}>
              gun kaldi
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <CalendarBlank size={12} /> Bitis Tarihi
              </div>
              <div className="text-sm font-bold text-slate-900">
                {new Date(data.end_date).toLocaleDateString("tr-TR")}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <Clock size={12} /> Yenileme Tutari
              </div>
              <div className="text-sm font-bold text-indigo-600">
                {sym}{data.amount?.toLocaleString("tr-TR")}
              </div>
            </div>
          </div>

          {/* Warning message */}
          <div className={`p-3 rounded-lg text-sm ${
            isCritical
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}>
            {isCritical
              ? "Suresinde yenileme yapilmazsa hesabiniz ve tum kiracileriniz askiya alinacak, yeni bakiye yuklemesi engellenecektir."
              : "Aboneliginizin kesintisiz devam etmesi icin lutfen zamaninda yenileme odemesi yapiniz."}
          </div>
        </div>

        <Button
          className={`w-full ${
            isCritical
              ? "bg-red-600 hover:bg-red-700"
              : "bg-amber-600 hover:bg-amber-700"
          } text-white`}
          onClick={handleDismiss}
          data-testid="subscription-warning-dismiss-btn"
        >
          Anladim
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionWarningPopup;
